import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/ui-kit";
import { api, formatApiError } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Search, Users, CalendarCheck } from "lucide-react";
import { StaffPhoto, STATUS_LABEL, STATUS_TONE } from "@/components/staff/StaffPhoto";
import { StaffForm } from "@/components/staff/StaffForm";
import { useClassOptions } from "@/components/ClassOptions";
import { StaffLeavePanel, StaffRolesPanel, StaffRequestsPanel } from "@/components/staff/StaffPanels";

const ALL = "__all__";

function ClassEdit({ s, onSaved }) {
  const [schoolTypes, setSchoolTypes] = useState([]);
  const [typing, setTyping] = useState(false);
  const [val, setVal] = useState("");
  useEffect(() => { api.get("/meta/school-types").then((r) => setSchoolTypes(r.data)).catch(() => {}); }, []);
  const opts = useClassOptions(s.branch_id, schoolTypes.flatMap((t) => t.classes));
  const list = Array.from(new Set([...opts.all, ...opts.groups, ...(s.assigned_class ? [s.assigned_class] : [])]));
  async function save(v) {
    try { await api.patch(`/staff-mgmt/staff/${s.id}/class`, { assigned_class: v }); toast.success(v ? `${s.name} → ${v}` : "Class removed"); setTyping(false); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  if (typing) return (
    <div className="flex gap-1 mt-2">
      <Input data-testid={`staff-class-new-${s.id}`} autoFocus value={val} onChange={(e) => setVal(e.target.value)} placeholder="Type class name" className="h-8 text-xs" />
      <Button size="sm" className="h-8 btn-brand" disabled={!val.trim()} onClick={() => save(val.trim())}>Save</Button>
    </div>
  );
  return (
    <Select value={s.assigned_class || "none"} onValueChange={(v) => (v === "__new" ? setTyping(true) : save(v === "none" ? "" : v))}>
      <SelectTrigger data-testid={`staff-class-${s.id}`} className="h-8 text-xs mt-2 bg-violet-50/50"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="none">No assigned class</SelectItem>{list.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}<SelectItem value="__new">+ Add a class not in the list</SelectItem></SelectContent>
    </Select>
  );
}

function StaffCard({ s, branch, onOpen, canEdit, onSaved }) {
  return (
    <div className="bg-white rounded-2xl border border-violet-100 p-4 hover:border-violet-400 hover:shadow-md transition-[border-color,box-shadow] duration-200">
    <button data-testid={`staff-card-${s.email}`} onClick={() => onOpen(s.id)} className="text-left w-full">
      <div className="flex items-center gap-3">
        <StaffPhoto fileId={s.photo_file_id} name={s.name} />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-slate-900 truncate">{s.name}</div>
          <div className="text-xs text-slate-500 font-mono">{s.employee_id || "—"}</div>
          <div className="text-xs text-slate-600 truncate">{s.designation || s.custom_role || ROLE_LABELS[s.role]}</div>
        </div>
        <Badge className={STATUS_TONE[s.employment_status]}>{STATUS_LABEL[s.employment_status]}</Badge>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-1.5 text-xs">
        <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100">{s.role === "staff" && s.custom_role ? s.custom_role : ROLE_LABELS[s.role]}</Badge>
        {s.assigned_class && <Badge variant="outline">{s.assigned_class}{s.assigned_section ? `-${s.assigned_section}` : ""}</Badge>}
        <span className="text-slate-500 truncate">{branch}</span>
      </div>
    </button>
    {canEdit && ["teacher", "coordinator", "staff"].includes(s.role) && <ClassEdit s={s} onSaved={onSaved} />}
    </div>
  );
}

export default function StaffPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = ["master_admin", "super_admin"].includes(user?.role);
  const canEdit = isAdmin || ["principal", "center_head"].includes(user?.role);
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [q, setQ] = useState("");
  const [branch, setBranch] = useState(ALL);
  const [desig, setDesig] = useState(ALL);
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("directory");
  const [reqRefresh, setReqRefresh] = useState(0);
  const load = () => api.get("/staff-mgmt/staff", { params: { q: q || undefined, branch_id: branch === ALL ? undefined : branch, designation: desig === ALL ? undefined : desig } })
    .then((r) => setStaff(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, branch, desig]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/staff-mgmt/designations").then((r) => setDesignations(r.data)).catch(() => {});
  }, [staff.length]);
  const bname = Object.fromEntries(branches.map((b) => [b.id, b.name]));
  const open = (id) => nav(`/staff/${id}`);
  return (
    <AppShell>
      <div data-testid="staff-page">
        <PageHeader eyebrow="Team" title="Staff Management" subtitle={`${staff.length} staff members ${isAdmin ? "across branches" : "at your branch"}`}>
          {canEdit && !adding && <Button data-testid="staff-add" className="btn-brand" onClick={() => setAdding(true)}><Plus size={16} className="mr-1" /> {isAdmin ? "Add staff" : "Request new staff"}</Button>}
        </PageHeader>
        {adding && <div className="mb-5"><StaffForm onSaved={(s) => { setAdding(false); if (s.request) { setReqRefresh((x) => x + 1); setTab("requests"); } else open(s.id); }} onCancel={() => setAdding(false)} /></div>}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white flex-wrap h-auto mb-4">
            <TabsTrigger value="directory" data-testid="staff-tab-directory">Directory</TabsTrigger>
            <TabsTrigger value="leave" data-testid="staff-tab-leave">Leave requests</TabsTrigger>
            {canEdit && <TabsTrigger value="requests" data-testid="staff-tab-requests">Staff requests</TabsTrigger>}
            {canEdit && <TabsTrigger value="roles" data-testid="staff-tab-roles">Roles</TabsTrigger>}
          </TabsList>
          <TabsContent value="directory">
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative flex-1 min-w-[220px]"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="staff-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, employee ID, designation or class" className="pl-9 bg-white" /></div>
              {isAdmin && <Select value={branch} onValueChange={setBranch}><SelectTrigger data-testid="staff-branch-filter" className="w-56 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ALL}>All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>}
              <Select value={desig} onValueChange={setDesig}><SelectTrigger data-testid="staff-designation-filter" className="w-52 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ALL}>All designations</SelectItem>{designations.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
              <Link to="/staff-attendance"><Button data-testid="staff-mark-attendance" variant="outline" className="bg-white"><CalendarCheck size={15} className="mr-1" /> Mark today's attendance</Button></Link>
            </div>
            {staff.length === 0 ? <div className="bg-white rounded-2xl p-12 text-center text-slate-500"><Users className="mx-auto text-violet-300 mb-2" size={40} />No staff found.</div> : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{staff.map((s) => <StaffCard key={s.id} s={s} branch={bname[s.branch_id]} onOpen={open} canEdit={canEdit} onSaved={load} />)}</div>
            )}
          </TabsContent>
          <TabsContent value="leave"><StaffLeavePanel /></TabsContent>
          {canEdit && <TabsContent value="requests"><StaffRequestsPanel isAdmin={isAdmin} refresh={reqRefresh} onApproved={load} /></TabsContent>}
          {canEdit && <TabsContent value="roles"><StaffRolesPanel staff={staff} onOpen={open} /></TabsContent>}
        </Tabs>
      </div>
    </AppShell>
  );
}
